/**
 * What a project cost, as rows rather than as one number.
 *
 * The project head has printed a spend figure for a while, and until this tab
 * there was no way to check it: `Expense.projectId` is a loose string with no
 * relation behind it, so the only screen that could show which costs made that
 * figure up was the expenses list, narrowed by hand to one project. This is that
 * list, scoped to one project and drawn the way the expenses section draws it -
 * same disc, same order, same status wording - because a row that looks
 * different here is a row an operator has to re-learn.
 *
 * Two of the section's columns are deliberately gone: client and project. Both
 * are constants inside a project page, and a column whose every cell repeats the
 * same value is a column taken away from the ones that differ.
 *
 * THE HEAD'S TOTAL IS THE ENDPOINT'S, not the page's. `sumCents` covers the
 * whole filtered set; the rows are a window over it (the route caps a page at
 * 200), so a footer that added up what is on screen would print a smaller number
 * in exactly the same typeface as the right one. It is also currency-blind, the
 * same way the expenses page's strip is: the aggregate sums the cents column
 * without grouping by currency, so a non-EUR expense would be added at face
 * value. Everything booked here today is in euros.
 *
 * Read-only apart from adding. Editing, deleting and rebilling all live on the
 * expenses page behind the same right, and a second place to rebill is a second
 * place to create an invoice nobody was expecting. Adding is here because this
 * is where the operator is standing when the cost lands.
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Receipt } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform } from "../../../lib/auth";
import { formatCents, getExpenses, type Expense } from "../../../lib/sales";
import { useCrmUser } from "../../shell/CrmUser";
import { Btn, Card, CardHead, EmptyState, StatusPill, fmtDate, listCard, useToast } from "../../ui";
import { ExpenseModal } from "../expenses/ExpenseModal";
// The one rule this tab shares with the expenses list rather than restating:
// rebilled beats billable beats neither. Two copies of it drift the first time
// somebody adds a state, and then the same expense reads as two things.
import { statusOf as expenseStatus } from "../expenses/ExpenseRow";

/** Category (with its description under it), date, vendor, amount, status. The
 *  expenses list's order, minus its client, project and menu tracks. */
const COLS = "minmax(0,2fr) .85fr 1fr .9fr 1fr";

/** How many rows one read brings back, and how much each "more" adds. */
const PAGE = 100;

/** The ceiling `expenses.routes.ts` puts on `pageSize`. The window stops growing
 *  here, and the footer keeps saying how much is left. Ten expenses is the whole
 *  imported history across every project, so this is a guardrail rather than a
 *  limit anybody is expected to meet. */
const MAX_ROWS = 200;

/** The column strip over the rows. */
function ListHead() {
  const t = useT();
  return (
    <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
      <span>{t("expenses.col.category")}</span>
      <span>{t("expenses.col.date")}</span>
      <span>{t("expenses.col.vendor")}</span>
      <span>{t("expenses.col.amount")}</span>
      <span>{t("expenses.col.status")}</span>
    </div>
  );
}

function ExpenseLine({ expense }: { expense: Expense }) {
  const t = useT();
  const status = expenseStatus(expense);

  return (
    // A div rather than a link, and no stretched `::after`: an expense has no
    // page of its own to reach, which is the same reason the expenses list draws
    // its rows this way.
    <div className="crm-list-row" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        {/* A tinted disc rather than an avatar: initials made out of a category
            name would read as a person who does not exist. */}
        <span className="crm-av crm-av-soft" aria-hidden>
          <Receipt className="size-4" />
        </span>
        <span className="min-w-0">
          <span className="crm-prow-n crm-trunc block">{expense.category}</span>
          <span className="crm-prow-s crm-trunc block">{expense.description ?? "-"}</span>
        </span>
      </span>

      <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(expense.spentAt)}</span>
      <span className="crm-mut crm-trunc text-[12.5px]">{expense.vendor ?? "-"}</span>

      {/* Net of VAT, carrying the row's own currency - the figure the project's
          spend is summed from, and the figure a rebilled line is written for. */}
      <span className="crm-tnum text-[13.25px] font-bold">
        {formatCents(expense.amountCents, expense.currency)}
      </span>

      <span>
        <StatusPill tone={status.tone}>{t(status.key)}</StatusPill>
      </span>
    </div>
  );
}

export function ProjectExpenses({
  projectId,
  clientId,
  onChange,
}: {
  projectId: string;
  /**
   * The project's client, for the form below.
   *
   * Passed in rather than read again here: the page already has the project, and
   * an expense booked to a project whose client it does not carry is the row
   * that makes a client's spend total disagree with the sum of its projects.
   */
  clientId: string;
  /** Tells the page frame that a counter above the tabs is now stale. */
  onChange?: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const canManage = userCanPlatform(useCrmUser(), "manage:crm.expenses");

  const [rows, setRows] = useState<Expense[]>([]);
  const [total, setTotal] = useState(0);
  // Both aggregates arrive with the page and cover the whole set, which is what
  // makes them safe to print in a head that sits over a window.
  const [sums, setSums] = useState({ net: 0, tax: 0 });
  const [limit, setLimit] = useState(PAGE);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getExpenses({ projectId, pageSize: limit });
      setRows(res.rows);
      setTotal(res.total);
      setSums({ net: res.sumCents, tax: res.taxSumCents });
    } catch {
      // An empty list and a failed read look the same on screen, so the failure
      // says so out loud rather than leaving the operator to read "no expenses"
      // on a project that has ten.
      toast(t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [projectId, limit, toast, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const addBtn = canManage ? (
    <Btn small tone="dark" onClick={() => setAdding(true)}>
      <Plus className="size-4" />
      {t("projects.newExpense")}
    </Btn>
  ) : null;

  // Only on the first read: a "more" click keeps the rows it already has under
  // the spinner instead of blanking the card the operator is reading.
  if (loading && rows.length === 0) {
    return (
      <div className="crm-card flex justify-center py-16 text-muted-foreground">
        <Loader2 className="size-6 animate-spin" />
      </div>
    );
  }

  return (
    <>
      <Card style={listCard(COLS)}>
        <CardHead
          title={t("projects.linkedExpenses")}
          // No figures over an empty list: "0,00 EUR net" under a card that says
          // nothing has been booked is the same statement made twice, once in a
          // way that looks like a measurement.
          sub={
            total > 0
              ? t("expenses.projectSum", {
                  net: formatCents(sums.net),
                  gross: formatCents(sums.net + sums.tax),
                })
              : undefined
          }
          actions={addBtn}
        />

        {rows.length === 0 ? (
          <EmptyState icon={Receipt} title={t("projects.noExpenses")} action={addBtn} />
        ) : (
          <>
            <ListHead />

            <div className="crm-rows pt-0">
              {rows.map((ex) => (
                <ExpenseLine key={ex.id} expense={ex} />
              ))}
            </div>

            {/* Drawn only when the window is actually closing over something.
                The head's total already counts the rows nobody can see, so the
                footer's job is to explain why those two disagree. */}
            {rows.length < total ? (
              <div className="crm-tfoot">
                {t("expenses.showing", { from: 1, to: rows.length, total })}
                {limit < MAX_ROWS ? (
                  <Btn
                    small
                    className="crm-push"
                    disabled={loading}
                    onClick={() => setLimit((l) => Math.min(MAX_ROWS, l + PAGE))}
                  >
                    {loading ? <Loader2 className="size-4 animate-spin" /> : null}
                    {t("common.more")}
                  </Btn>
                ) : null}
              </div>
            ) : null}
          </>
        )}
      </Card>

      {/* The expenses section's own form, opened with this project and its client
          already in it. A second expense form here would be a second place for
          the VAT field, the billable flag and the project link to disagree about
          what an expense is. */}
      {adding ? (
        <ExpenseModal
          expense={null}
          preset={{ clientId, projectId }}
          onClose={() => setAdding(false)}
          onSaved={() => {
            void load();
            onChange?.();
          }}
        />
      ) : null}
    </>
  );
}
